/**
 * Targets & Pacing Section
 * ========================
 * Executive summary section showing key pacing metrics at a glance.
 * Includes settings gear to edit targets and link to full pacing page.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, 
  ExternalLink, 
  TrendingUp, 
  TrendingDown,
  Minus,
  AlertCircle,
  CheckCircle2,
  Clock,
  Calendar,
  MapPin
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { format } from 'date-fns';
import MonthlyTargetsEditor from './MonthlyTargetsEditor';

const STATUS_STYLES = {
  // 3-tier status system
  on_pace: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  on_track: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  at_risk: 'text-yellow-600 dark:text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
  below_min: 'text-red-600 dark:text-red-400 bg-red-500/10 border-red-500/30',
  behind: 'text-red-600 dark:text-red-400 bg-red-500/10 border-red-500/30',
  future: 'text-blue-600 dark:text-blue-400 bg-blue-500/10 border-blue-500/30',
  no_data: 'text-slate-500 dark:text-slate-400 bg-slate-500/10 border-slate-500/30',
  // Legacy mappings
  green: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  yellow: 'text-yellow-600 dark:text-yellow-400 bg-yellow-500/10 border-yellow-500/30',
  red: 'text-red-600 dark:text-red-400 bg-red-500/10 border-red-500/30'
};

const STATUS_ICONS = {
  on_pace: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  on_track: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  at_risk: <AlertCircle className="h-4 w-4 text-yellow-500" />,
  below_min: <AlertCircle className="h-4 w-4 text-red-500" />,
  behind: <AlertCircle className="h-4 w-4 text-red-500" />,
  future: <Clock className="h-4 w-4 text-blue-500" />,
  no_data: <Minus className="h-4 w-4 text-slate-400" />,
  green: <CheckCircle2 className="h-4 w-4 text-emerald-500" />,
  yellow: <AlertCircle className="h-4 w-4 text-yellow-500" />,
  red: <AlertCircle className="h-4 w-4 text-red-500" />
};

const STATUS_LABELS = {
  on_pace: 'Goal Met',
  on_track: 'Goal Met',
  at_risk: 'Needs Attention',
  below_min: 'Below Minimum',
  behind: 'Below Minimum',
  future: 'Future Period',
  no_data: 'No Data',
  green: 'Goal Met',
  yellow: 'Needs Attention',
  red: 'Below Minimum'
};

const PacingTile = ({ label, value, target, minimum, status, projection, isFuture, format = 'number', suffix = '' }) => {
  const formatValue = (val) => {
    if (val === null || val === undefined) return '-';
    
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(val);
      case 'percent':
        return `${Number(val).toFixed(1)}%`;
      case 'multiplier':
        return Number(val).toFixed(2);
      default:
        // Round to integer for count metrics (leads, appointments, sales, etc.)
        return Math.round(Number(val)).toLocaleString();
    }
  };
  
  const formatTarget = (val) => {
    if (val === null || val === undefined) return '-';
    
    switch (format) {
      case 'currency':
        // Compact format for targets
        if (val >= 1000000) {
          return `$${(val / 1000000).toFixed(1)}M`;
        } else if (val >= 1000) {
          return `$${(val / 1000).toFixed(0)}K`;
        }
        return `$${Math.round(val)}`;
      default:
        return Math.round(Number(val)).toLocaleString();
    }
  };
  
  const styles = STATUS_STYLES[status] || STATUS_STYLES.at_risk;
  const icon = STATUS_ICONS[status] || STATUS_ICONS.at_risk;
  const statusLabel = STATUS_LABELS[status] || 'Unknown';
  
  // Check if minimum is met (for yellow "Needs Attention" status)
  const minMet = status === 'at_risk' && value >= minimum;
  
  // For future periods, show projection (which equals target) instead of actual
  const displayValue = isFuture || status === 'future' ? projection || target : value;
  const displayLabel = isFuture || status === 'future' ? 'Target' : null;
  
  return (
    <div 
      className={`p-4 rounded-lg border ${styles} transition-all hover:shadow-sm`}
      data-testid={`pacing-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-muted-foreground">{label}</span>
        <div className="flex items-center gap-1" title={statusLabel}>
          {icon}
        </div>
      </div>
      <p className="text-2xl font-bold">
        {formatValue(displayValue)}{suffix}
      </p>
      {displayLabel && (
        <p className="text-xs text-blue-500 -mt-1 mb-1">(Target for period)</p>
      )}
      <div className="flex justify-between items-center mt-1">
        <p className="text-xs text-muted-foreground">
          T: {formatTarget(target)}{suffix}
        </p>
        {minimum !== undefined && minimum > 0 && (
          <p className="text-xs text-orange-500" title="Minimum threshold">
            M: {formatTarget(minimum)}{suffix}
          </p>
        )}
      </div>
      <div className="flex items-center justify-between mt-1">
        <p className="text-xs font-medium" title={`Status: ${statusLabel}`}>
          {statusLabel}
        </p>
        {minMet && (
          <span className="text-xs bg-green-500/20 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
            Min Met ✓
          </span>
        )}
      </div>
    </div>
  );
};

const TargetsPacingSection = ({ 
  pacingData, 
  loading, 
  user, 
  onTargetsUpdated,
  showViewFullButton = true,
  hideHeader = false,
  // New props for filter controls
  selectedPeriod = 'mtd',
  onPeriodChange,
  selectedMarket = 'all',
  onMarketChange,
  customDateRange = null,
  onCustomDateChange,
  markets = []
}) => {
  const navigate = useNavigate();
  const [showTargetsModal, setShowTargetsModal] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Check if user can edit targets - enhanced check for role detection
  const canEditTargets = (() => {
    const role = user?.role || user?.userRole || '';
    const email = user?.email || '';
    
    // Super admin email override
    const isSuperAdminEmail = email.toLowerCase() === 'rmarch@rhinoshield.com';
    
    const hasEditRole = ['super_admin', 'admin', 'owner', 'marketing', 'marketing_manager'].includes(role);
    
    // Debug log
    console.log('[TargetsPacingSection] User role check:', { 
      role, 
      email: email.substring(0, 10) + '...', 
      canEdit: hasEditRole || isSuperAdminEmail 
    });
    
    return hasEditRole || isSuperAdminEmail;
  })();
  
  const period = pacingData?.period || {};
  const metrics = pacingData?.metrics || {};
  const periodLabel = period?.label || pacingData?.active_month_name || 'This Month';
  
  // Key "killer" metrics
  const revenueMetric = metrics.revenue || {};
  const gapToTarget = revenueMetric.gap_to_target || 0;
  
  // Period options for the toggle
  const periodOptions = [
    { value: 'week', label: 'Week' },
    { value: 'mtd', label: 'Month' },
    { value: 'q1', label: 'Q1' },
    { value: 'q2', label: 'Q2' },
    { value: 'q3', label: 'Q3' },
    { value: 'q4', label: 'Q4' },
    { value: 'ytd', label: 'Year' },
  ];
  
  // Available markets from backend or props
  const availableMarkets = markets.length > 0 ? markets : (pacingData?.available_markets || []);
  
  return (
    <div className="space-y-4" data-testid="targets-pacing-section">
      {/* Section Header with Filters */}
      <div className="flex flex-col gap-3">
        {/* Top Row: Title and Settings */}
        {!hideHeader && (
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Targets & Pacing
              </h3>
            </div>
            
            <div className="flex items-center gap-2">
              {canEditTargets && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowTargetsModal(true)}
                  data-testid="edit-targets-btn"
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Settings
                </Button>
              )}
            </div>
          </div>
        )}
        
        {/* Filter Row: Period Toggle, Market, Custom Date */}
        <div className="flex flex-wrap items-center gap-3 pb-2 border-b dark:border-slate-700">
          {/* Period Toggle Buttons */}
          <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            {periodOptions.map(option => (
              <button
                key={option.value}
                onClick={() => onPeriodChange?.(option.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  selectedPeriod === option.value
                    ? 'bg-white dark:bg-slate-700 text-primary shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
                data-testid={`period-toggle-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
          
          {/* Market Filter */}
          <Select value={selectedMarket} onValueChange={onMarketChange}>
            <SelectTrigger className="w-[160px]" data-testid="market-filter-select">
              <MapPin className="h-4 w-4 mr-1 text-muted-foreground" />
              <SelectValue placeholder="All Markets" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Markets</SelectItem>
              {availableMarkets.map(market => (
                <SelectItem 
                  key={typeof market === 'string' ? market : market.id || market.slug} 
                  value={typeof market === 'string' ? market : market.slug || market.id}
                >
                  {typeof market === 'string' ? market.charAt(0).toUpperCase() + market.slice(1) : market.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Custom Date Range Picker */}
          <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={`${customDateRange ? 'border-primary text-primary' : ''}`}
                data-testid="custom-date-btn"
              >
                <Calendar className="h-4 w-4 mr-1" />
                {customDateRange 
                  ? `${format(new Date(customDateRange.start), 'MMM d')} - ${format(new Date(customDateRange.end), 'MMM d')}`
                  : 'Custom Date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarComponent
                mode="range"
                selected={{
                  from: customDateRange?.start ? new Date(customDateRange.start) : undefined,
                  to: customDateRange?.end ? new Date(customDateRange.end) : undefined
                }}
                onSelect={(range) => {
                  if (range?.from && range?.to) {
                    onCustomDateChange?.({
                      start: format(range.from, 'yyyy-MM-dd'),
                      end: format(range.to, 'yyyy-MM-dd')
                    });
                    setDatePickerOpen(false);
                  } else if (range?.from) {
                    onCustomDateChange?.({
                      start: format(range.from, 'yyyy-MM-dd'),
                      end: null
                    });
                  }
                }}
                initialFocus
              />
              {customDateRange && (
                <div className="p-2 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-muted-foreground"
                    onClick={() => {
                      onCustomDateChange?.(null);
                      setDatePickerOpen(false);
                    }}
                  >
                    Clear Custom Range
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          
          {/* Period Info */}
          <div className="ml-auto text-sm text-muted-foreground flex items-center gap-2">
            <span className="font-medium text-foreground">{periodLabel}</span>
            <span>•</span>
            <span>{period.days_elapsed || 0} of {period.days_total || 0} days ({period.elapsed_percent || 0}%)</span>
          </div>
        </div>
      </div>
      <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {period?.is_future ? 'Target Revenue' : 'On-Pace Revenue Projection'}
              </p>
              <p className="text-3xl font-bold text-primary">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0
                }).format(revenueMetric.pace_projection || 0)}
              </p>
              {period?.is_future && (
                <p className="text-xs text-blue-500 mt-1">Future period - showing target</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-sm font-medium text-muted-foreground">
                {period?.is_future ? 'Minimum' : 'Gap to Target'}
              </p>
              {period?.is_future ? (
                <p className="text-2xl font-bold text-orange-500">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0
                  }).format(revenueMetric.minimum || 0)}
                </p>
              ) : (
                <p className={`text-2xl font-bold ${gapToTarget >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {gapToTarget >= 0 ? '+' : ''}
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 0
                  }).format(gapToTarget)}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Pacing Tiles Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
        <PacingTile
          label="Leads"
          value={metrics.leads?.actual}
          target={metrics.leads?.target}
          minimum={metrics.leads?.minimum}
          status={metrics.leads?.status}
          projection={metrics.leads?.pace_projection}
          isFuture={period?.is_future}
        />
        <PacingTile
          label="Appointments"
          value={metrics.appointments?.actual}
          target={metrics.appointments?.target}
          minimum={metrics.appointments?.minimum}
          status={metrics.appointments?.status}
          projection={metrics.appointments?.pace_projection}
          isFuture={period?.is_future}
        />
        <PacingTile
          label="Sales"
          value={metrics.sales?.actual}
          target={metrics.sales?.target}
          minimum={metrics.sales?.minimum}
          status={metrics.sales?.status}
          projection={metrics.sales?.pace_projection}
          isFuture={period?.is_future}
        />
        <PacingTile
          label="Revenue"
          value={metrics.revenue?.actual}
          target={metrics.revenue?.target}
          minimum={metrics.revenue?.minimum}
          status={metrics.revenue?.status}
          projection={metrics.revenue?.pace_projection}
          isFuture={period?.is_future}
          format="currency"
        />
        <PacingTile
          label="Ad Spend"
          value={metrics.spend?.actual}
          target={metrics.spend?.target}
          minimum={metrics.spend?.minimum}
          status={metrics.spend?.status}
          projection={metrics.spend?.pace_projection}
          isFuture={period?.is_future}
          format="currency"
        />
        <PacingTile
          label="ROAS"
          value={metrics.roas?.actual}
          target={metrics.roas?.target}
          minimum={metrics.roas?.minimum}
          status={metrics.roas?.status}
          projection={metrics.roas?.pace_projection}
          isFuture={period?.is_future}
          format="multiplier"
          suffix="x"
        />
      </div>
      
      {/* ROAS Note */}
      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Clock className="h-3 w-3" />
        ROAS based on manual spend • Last updated: {new Date(pacingData?.generated_at).toLocaleString() || 'N/A'}
      </p>
      
      {/* Monthly Targets Editor Modal */}
      <MonthlyTargetsEditor
        isOpen={showTargetsModal}
        onClose={() => setShowTargetsModal(false)}
        onSave={onTargetsUpdated}
        user={user}
        canEdit={canEditTargets}
      />
    </div>
  );
};

export default TargetsPacingSection;
